import { dayPlus } from "@bl/domain";
import { useMemo } from "react";
import { useShortfall } from "./api";
import { showDate, today } from "./format";
import { useLayout } from "./layout/LayoutProvider";

/** Through every brew planned, however far ahead. */
const EVERY_BREW = "9999-12-31";

/** The day an item first falls short, and whether that is soon enough to order for. */
export interface ShortFrom {
  on: string;
  soon: boolean;
}

/**
 * When each item the schedule runs short of first falls short: the day of the first brew it does not
 * cover. Inventory and an item's page say it beside a below-zero Available, so they answer "do we
 * have enough?" with a when, as the Calendar and To order do (the critique, 2026-10-06, found four
 * answers and none saying by when). It is soon, and red, inside the window To order looks at (a month
 * unless the brewery changes it); later, it is said without alarm, since nothing needs ordering yet.
 */
export function useShortFrom(): (itemId: string) => ShortFrom | null {
  const { data } = useShortfall(EVERY_BREW);
  const { layout } = useLayout();
  const until = dayPlus(today(), layout.schedule.ahead);
  const from = useMemo(
    () => new Map((data?.items ?? []).map((i) => [i.item.id, i.neededBy])),
    [data],
  );
  return (itemId) => {
    const on = from.get(itemId);
    return on === undefined ? null : { on, soon: on <= until };
  };
}

/** The soonest of several items' shortfalls, as a variety's row shows them together. */
export function soonestOf(all: (ShortFrom | null)[]): ShortFrom | null {
  return all.reduce<ShortFrom | null>((a, b) => (b && (!a || b.on < a.on) ? b : a), null);
}

/**
 * What a recipe line's stock comes to, said the same way on the recipe's page and in the builder (the
 * re-critique, 2026-10-07: the builder still said "Short" in red with no date). Short of what is on hand
 * now is red; short only once the planned brews take theirs is red inside To order's window and a
 * warning beyond it, with the day it starts.
 */
export function shortVerdict(
  enough: boolean,
  now: boolean,
  from: ShortFrom | null,
): { tone: "ok" | "danger" | "warn"; when: string | null } {
  if (enough) return { tone: "ok", when: null };
  if (now) return { tone: "danger", when: "Not enough on hand to brew it now" };
  return {
    tone: from?.soon ? "danger" : "warn",
    when: from
      ? `Runs short from ${showDate(from.on)} with the brews planned`
      : "Short once the planned brews take theirs",
  };
}
